import { useEffect, useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { SearchInput } from "../components/ui/Fields";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";
import type { MallItem, MallUserSearchResult, MallUserInventoryEntry } from "../api/types";

/**
 * Round 45 client item 6 — "Admin Panel: allow admins to send mall items
 * directly to any user (select user, select item, set duration, send);
 * item should appear in that user's Bag with expiry info." Same real
 * select-user → select-item → send flow as SendMedalPage, over the new
 * routes/admin/mall-items.js grant/search-users/inventory endpoints —
 * a direct, FREE insert into the real user_mall_items table (never
 * buy_mall_item(), which debits coins), so this never charges the
 * recipient.
 */
export function SendMallItemPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<MallUserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<MallUserSearchResult | null>(null);
  const [inventory, setInventory] = useState<MallUserInventoryEntry[]>([]);
  const [items, setItems] = useState<MallItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [durationDays, setDurationDays] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [notifyUser, setNotifyUser] = useState(true);
  const { showToast } = useToast();

  useEffect(() => {
    apiRequest<MallItem[]>("/admin/mall-items").then((rows) => setItems(rows.filter((i) => i.is_active))).catch(() => {});
  }, []);

  async function search() {
    if (!query.trim()) return;
    setIsSearching(true);
    setResults([]);
    try {
      const rows = await apiRequest<MallUserSearchResult[]>("/admin/mall-items/search-users", { params: { query: query.trim() } });
      setResults(rows);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Search failed", "error");
    } finally {
      setIsSearching(false);
    }
  }

  async function selectUser(user: MallUserSearchResult) {
    setSelectedUser(user);
    setResults([]);
    setSelectedItemId(null);
    setDurationDays("");
    try {
      const rows = await apiRequest<MallUserInventoryEntry[]>("/admin/mall-items/inventory", { params: { user_id: user.id } });
      setInventory(rows);
    } catch {
      setInventory([]);
    }
  }

  const selectedItem = items.find((i) => i.id === selectedItemId) ?? null;

  async function send() {
    if (!selectedUser || !selectedItem) return;
    setIsSending(true);
    try {
      await apiRequest(`/admin/mall-items/${selectedUser.id}/grant`, {
        method: "POST",
        body: {
          itemId: selectedItem.id,
          durationDays: durationDays.trim() ? Number(durationDays.trim()) : undefined,
          notifyUser,
        },
      });
      showToast(`Sent ${selectedItem.name} to ${selectedUser.username}`, "success");
      const rows = await apiRequest<MallUserInventoryEntry[]>("/admin/mall-items/inventory", { params: { user_id: selectedUser.id } });
      setInventory(rows);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not send item", "error");
    } finally {
      setIsSending(false);
    }
  }

  const heldCount = (itemId: number) => inventory.filter((row) => row.item_id === itemId).length;

  return (
    <AppShell title="Send Mall Item">
      <p className="mb-4 max-w-lg text-xs text-ink-faint">
        Search by username or public ID, pick a real user, then select an active Mall item and (optionally) a
        duration in days to override the item's own default — leave it blank to use the item's configured
        duration (or permanent, if it has none). Sending never charges the recipient's coins.
      </p>

      <div className="mb-4 flex items-end gap-2">
        <SearchInput value={query} onChange={setQuery} placeholder="Username or public ID..." />
        <Button variant="secondary" onClick={search} isLoading={isSearching} disabled={!query.trim()}>Search</Button>
      </div>

      {results.length > 0 && (
        <Card className="mb-4 divide-y divide-surface-border">
          {results.map((user) => (
            <button
              key={user.id}
              onClick={() => selectUser(user)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-raised"
            >
              <Avatar src={user.avatar_url} className="h-8 w-8 rounded-full object-cover" />
              <div>
                <p className="text-sm font-medium text-ink">{user.username}</p>
                <p className="text-xs text-ink-faint">ID: {user.user_id}</p>
              </div>
            </button>
          ))}
        </Card>
      )}

      {selectedUser && (
        <Card className="p-5">
          <div className="mb-4 flex items-center gap-3">
            <Avatar src={selectedUser.avatar_url} className="h-10 w-10 rounded-full object-cover" />
            <div>
              <p className="text-sm font-semibold text-ink">{selectedUser.username}</p>
              <p className="text-xs text-ink-faint">ID: {selectedUser.user_id}</p>
            </div>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setSelectedUser(null)}>Change user</Button>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {items.map((item) => {
              const held = heldCount(item.id);
              const selected = selectedItemId === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setSelectedItemId(item.id)}
                  className={`flex flex-col items-center gap-2 rounded-lg border p-3 text-center ${
                    selected ? "border-accent" : "border-surface-border"
                  }`}
                >
                  {item.png_asset_url ? <img src={item.png_asset_url} alt={item.name} className="h-12 w-12 object-contain" /> : <div className="h-12 w-12 rounded-md bg-surface-raised" />}
                  <p className="text-xs font-medium text-ink">{item.name}</p>
                  {held > 0 && <p className="text-[10px] text-ink-faint">Owns {held}</p>}
                </button>
              );
            })}
          </div>

          {selectedItem && (
            <div className="flex items-end gap-3 border-t border-surface-border pt-4">
              <div>
                <label className="mb-1 block text-xs text-ink-faint">
                  Duration (days) — blank = item default{selectedItem.duration_days ? ` (${selectedItem.duration_days}d)` : " (permanent)"}
                </label>
                <input
                  type="number"
                  min="0"
                  value={durationDays}
                  onChange={(e) => setDurationDays(e.target.value)}
                  className="w-32 rounded-md border border-surface-border bg-surface px-3 py-2 text-sm text-ink"
                  placeholder={selectedItem.duration_days ? String(selectedItem.duration_days) : "Permanent"}
                />
              </div>
              <div className="mb-2">
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={notifyUser} onChange={(e) => setNotifyUser(e.target.checked)} />
                  Notify user (System Sender message) — {notifyUser ? "ON" : "OFF: delivered silently"}
                </label>
              </div>
              <Button onClick={send} isLoading={isSending}>Send {selectedItem.name}</Button>
            </div>
          )}
        </Card>
      )}
    </AppShell>
  );
}
